import { useBoolean } from 'ahooks';
import { isNumber } from 'lodash';
import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router';
import useClipboard from 'react-use-clipboard';
import { ProjectId, SpaceId, SpaceTabId } from 'api/models';
import { Space, SpaceTab } from 'api/space';
import { UpdateSpaceTabPayload } from 'api/space/space-tab';
import { toastService } from 'components/molecules/toast';
import { SpaceTab as SpaceTabT } from 'data/space-tabs';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useTranslation } from 'hooks/use-translation';
import { getProjectSpacesQueryKey } from 'queries';
import {
  useSpaceTabDeleteMutation,
  useSpaceTabMutation,
} from 'queries/project-spaces';
import { getSpaceDetailUrl, getSpaceTabDetailUrl } from 'utils/urls';

export const useTabMoreActions = ({
  spaceTab,
  projectId,
}: {
  spaceTab: SpaceTab | SpaceTabT;
  projectId: number;
}) => {
  const title = useTranslation('space_tab.delete.confirm.title');
  const content = useTranslation('space_tab.delete.confirm.message');
  const message = useTranslation('copied');
  const description = useTranslation('space_tab.copied.description');

  const { spaceTabId, space } = spaceTab;

  const spaceId = (space as Space)?.spaceId ?? space;
  const [isModalVisible, { setTrue, setFalse }] = useBoolean(false);

  const history = useHistory();
  const queryClient = useQueryClient();

  const spaceUrl = getSpaceTabDetailUrl(
    SpaceTabId(projectId, spaceId, spaceTabId),
  );
  const [, copyToClipboard] = useClipboard(window.location.origin + spaceUrl);

  const { mutateAsync: deleteSpaceTabMutation } = useSpaceTabDeleteMutation();

  const handleDelete = async () => {
    if (isNumber(spaceTabId)) {
      return deleteSpaceTabMutation(spaceTabId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries(
            getProjectSpacesQueryKey(ProjectId(projectId)),
          );

          history.push(getSpaceDetailUrl(SpaceId(projectId, spaceId)));
        },
      });
    }

    return null;
  };

  const handleCopy = () => {
    copyToClipboard();
    toastService({
      title: message,
      message: description,
    }).success();
  };

  const deleteConfirmation = useDeleteConfirmModal({
    title,
    content,
    onOk: handleDelete,
  });

  const { mutateAsync, isLoading } = useSpaceTabMutation(spaceTab.spaceTabId);
  const handleOnFinish = useCallback(
    async (values: UpdateSpaceTabPayload) => {
      const { url, spaceTabTitle } = values;

      if (url !== spaceTab.url || spaceTabTitle !== spaceTab.spaceTabTitle) {
        await mutateAsync(
          { url, spaceTabTitle },
          {
            onSuccess: async () => {
              await queryClient.invalidateQueries(
                getProjectSpacesQueryKey(ProjectId(projectId)),
              );
              setFalse();
            },
          },
        );
      } else {
        setFalse();
      }
    },
    [projectId, setFalse, mutateAsync, queryClient, spaceTab],
  );

  return {
    isModalVisible,
    isLoading,
    setTrue,
    setFalse,
    handleCopy,
    deleteConfirmation,
    handleOnFinish,
  };
};
